import dayjs from 'dayjs';
import { t } from '../../i18next';
import { getAllDropdowns } from '../services/allDropdownsRegistry';

function hasValue(value) {
  return !(value === null || value === undefined || value === '');
}

export function customValidator(props) {
  const { allColumnDefs, row, SetISCellRequired, language = 'en' } = props;
  const resolvedDropdowns = getAllDropdowns();

  const validate = allColumnDefs.reduce(
    (result, field) => {
      let error = fieldValidator(field, row[field.attrInternalName], language);
      if (!error && SetISCellRequired) {
        const label = field[`attrDisplayName${language}`];
        const required = SetISCellRequired({
          field: field.attrInternalName,
          required: field.attrMandatory,
          row,
          allDropdowns: resolvedDropdowns,
        });
        if (required && !hasValue(row[field.attrInternalName])) {
          error = { field: label, type: 'required', msg: t('required') };
        }
      }

      if (error) {
        result.isValid = false;
        result.errors[field.attrInternalName] = error;
      }
      return result;
    },
    { isValid: true, errors: {} },
  );
  return validate;
}

export const valueFormatter = (value) => {
  if (!value && value !== 0) {
    return null;
  }
  const number = parseFloat(value);
  const parts = value.split('.');
  if (!parts[1]) {
    return parts[0];
  }
  if (parts[1]?.length <= 7 || isNaN(number)) {
    return value;
  } else {
    const roundedNumber = number.toFixed(7);
    return roundedNumber.replace(/\.?0+$/, '');
  }
};

export function fieldValidator(field, value, lng) {
  const {
    attrDataType: type,
    attrMinValue: colMinValue,
    attrMaxValue: colMaxValue,
    attrMandatory: required,
    editable,
  } = field;
  if (!editable) {
    return null;
  }

  const label = field[`attrDisplayName${lng}`];
  if (!required && !value) {
    return null;
  }
  const cvCheck = commonValidator({ required, type, value, label });
  if (cvCheck) {
    return cvCheck;
  }
  const booleanCheck = boolenAndSelectValidator({ required, type, value, label });
  if (booleanCheck) {
    return booleanCheck;
  }
  const multiSelectCheck = multiSelectValidator({ required, type, value, label });
  if (multiSelectCheck) {
    return multiSelectCheck;
  }

  if (type === 'number' && isNaN(value)) {
    return { field: label, type: 'isNan', msg: t('isNaN') };
  }

  const dateData = dateValidator({ type, value, label });
  if (dateData) {
    return dateData;
  }
  const minData = minValidator({ label, type, value, colMinValue });
  if (minData) {
    return minData;
  }
  const maxData = maxValidator({ label, type, value, colMaxValue });
  if (maxData) {
    return maxData;
  }

  return null;
}
function commonValidator(props) {
  const { required, type, value, label } = props;
  const newValue = typeof value === 'string' ? value.trim() : value;
  if (
    required &&
    type !== 'boolean' &&
    type !== 'checkBox' &&
    type !== 'singleSelect' &&
    type !== 'multiSelect' &&
    !newValue
  ) {
    return { field: label, type: 'required', msg: t('required') };
  }
  return null;
}

function boolenAndSelectValidator(props) {
  const { required, type, value, label } = props;

  const isEmpty =
    value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

  if (
    required &&
    (type === 'boolean' || type === 'singleSelect' || type === 'checkBox') &&
    isEmpty
  ) {
    return { field: label, type: 'required', msg: t('required') };
  }
  return null;
}

function multiSelectValidator(props) {
  const { required, type, value, label } = props;
  if (required && type === 'multiSelect' && !value?.length) {
    return { field: label, type: 'required', msg: t('required') };
  }
}
function dateValidator(props) {
  const { type, value, label } = props;
  if (type !== 'date') {
    return null;
  }
  let error = { field: label, type: 'invalid', msg: t('invalid_date') };
  const constructorString = value?.constructor.toString();
  if (dayjs(value).isValid() || constructorString.indexOf('Date') > -1) {
    error = null;
  }
  return error;
}

export function maxValidator(props) {
  const { label, type, value, colMaxValue } = props;
  if (!colMaxValue) {
    return null;
  }
  if (type === 'string' && value?.length > colMaxValue) {
    return { field: label, type: 'max', msg: t('maximun_character', { max: colMaxValue }) };
  } else if (type === 'number' && value > colMaxValue) {
    return { field: label, type: 'max', msg: t('maximun_limit', { max: colMaxValue }) };
  } else if (type === 'date') {
    const year = dayjs(value).get('year');
    if (year > colMaxValue) {
      return { field: label, type: 'max', msg: t('maximun_date', { max: colMaxValue }) };
    }
  }
  return null;
}

function minValidator(props) {
  const { label, type, value, colMinValue } = props;
  if (!colMinValue) {
    return null;
  }

  if (type === 'string' && value?.length < colMinValue) {
    return { field: label, type: 'min', msg: t('minimum_character', { min: colMinValue }) };
  } else if (type === 'number' && value < colMinValue) {
    return { field: label, type: 'min', msg: t('minimum_limit', { min: colMinValue }) };
  } else if (type === 'date') {
    const year = dayjs(value).get('year');
    if (year < colMinValue) {
      return { field: label, type: 'min', msg: t('minimum_date', { min: colMinValue }) };
    }
  }
  return null;
}
export const applyStringConditions = ({ colDef, error, newValue }) => {
  if (
    (!error || newValue === '') &&
    (newValue.length >= colDef.attrMinValue || colDef.attrMinValue === null) &&
    (newValue.length <= colDef.attrMaxValue || colDef.attrMaxValue === null)
  ) {
    return true;
  }
  return false;
};
export const applyNumberConditions = ({ colDef, error, newValue }) => {
  if (
    (!error || newValue === '') &&
    !isNaN(!error && +newValue) &&
    (+newValue >= colDef.attrMinValue || colDef.attrMinValue === null) &&
    (+newValue <= colDef.attrMaxValue || colDef.attrMaxValue === null)
  ) {
    return true;
  }
  return false;
};
